import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_COLORS,
  type AppointmentStatus,
} from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import type { WeekAppointment } from "@/lib/dashboard-week";
import { AppointmentDetailDialog } from "@/components/dashboard2/AppointmentDetailDialog";

type Appointment = {
  id: string;
  procedure: string;
  date: string | Date;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  notes?: string | null;
  dentistName?: string | null;
};

export function PatientAppointmentsTab({ patientId }: { patientId: string }) {
  const query = trpc.appointment.listByPatient.useQuery({ patientId, take: 100 });
  // Mesmo diálogo da Agenda e do Início — sem o link pro paciente, que aqui seria redundante.
  const [selecionada, setSelecionada] = useState<WeekAppointment | null>(null);

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  const appointments = (query.data ?? []) as Appointment[];

  if (appointments.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-zinc-500">
        Nenhuma consulta registrada pra esse paciente.
      </div>
    );
  }

  return (
    <div className="relative space-y-3">
      <div className="absolute bottom-0 left-4 top-0 w-px bg-zinc-100" aria-hidden />
      {appointments.map((a) => {
        const color = APPOINTMENT_STATUS_COLORS[a.status];
        return (
          <div key={a.id} className="relative pl-10">
            <div
              className="absolute left-2.5 top-3 h-3 w-3 rounded-full border-2 border-white shadow"
              style={{ backgroundColor: color }}
            />
            <button
              type="button"
              onClick={() => setSelecionada(a as unknown as WeekAppointment)}
              className="w-full rounded-lg border border-zinc-100 bg-white p-4 text-left shadow-sm transition-all hover:border-zinc-300 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-zinc-900">
                    {a.procedure}
                  </p>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {format(new Date(a.date), "dd MMM yyyy", { locale: ptBR })} •{" "}
                    {a.startTime} – {a.endTime}
                    {a.dentistName ? ` • ${a.dentistName}` : ""}
                  </p>
                </div>
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium text-white"
                  style={{ backgroundColor: color }}
                >
                  {APPOINTMENT_STATUS_LABELS[a.status]}
                </span>
              </div>
              {a.notes && (
                <p className="mt-2 whitespace-pre-wrap border-t border-zinc-100 pt-2 text-xs text-zinc-600">
                  {a.notes}
                </p>
              )}
            </button>
          </div>
        );
      })}

      <AppointmentDetailDialog
        appointment={selecionada}
        onClose={() => setSelecionada(null)}
        esconderLinkDoPaciente
      />
    </div>
  );
}
